import React, { useRef, useEffect } from 'react';
import { ScrollArea } from '../ui/scroll-area';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Phone, Mail, MessageCircle, PhoneCall, Voicemail, Bot,
  Play, Pause, Download, Clock, CheckCircle, AlertCircle,
  Briefcase, ArrowRight
} from 'lucide-react';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';

const MessageThread = ({ messages, loading, customer }) => {
  const scrollRef = useRef(null);
  
  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);
  
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Group messages by date
  const groupedMessages = messages.reduce((groups, msg) => {
    const date = new Date(msg.created_at).toDateString();
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});
  
  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr);
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'EEEE, MMMM d, yyyy');
  };
  
  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'sms': return <MessageCircle className="h-3 w-3" />;
      case 'email': return <Mail className="h-3 w-3" />;
      case 'call': return <PhoneCall className="h-3 w-3" />;
      case 'voicemail': return <Voicemail className="h-3 w-3" />;
      default: return <MessageCircle className="h-3 w-3" />;
    }
  };
  
  const getChannelColor = (channel) => {
    switch (channel) {
      case 'sms': return 'text-green-500';
      case 'email': return 'text-blue-500';
      case 'call': return 'text-orange-500';
      case 'voicemail': return 'text-purple-500';
      default: return 'text-gray-500';
    }
  };
  
  return (
    <ScrollArea className="flex-1 p-4" ref={scrollRef}>
      <div className="space-y-4">
        {Object.entries(groupedMessages).map(([dateStr, dateMessages]) => (
          <div key={dateStr}>
            {/* Date Divider */}
            <div className="flex items-center gap-4 my-4">
              <div className="flex-1 h-px bg-border"></div>
              <span className="text-xs text-muted-foreground font-medium">
                {formatDateHeader(dateStr)}
              </span>
              <div className="flex-1 h-px bg-border"></div>
            </div>
            
            {/* Messages */}
            {dateMessages.map((item) => {
              // System Event
              if (item.item_type === 'event') {
                return (
                  <div key={item.id} className="flex items-center justify-center my-3">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-xs text-muted-foreground">
                      {item.event_type === 'job_created' && <Briefcase className="h-3 w-3" />}
                      {item.event_type === 'status_changed' && <ArrowRight className="h-3 w-3" />}
                      {item.event_type === 'message_linked' && <CheckCircle className="h-3 w-3" />}
                      {item.event_type === 'conversation_created' && <Bot className="h-3 w-3" />}
                      <span>{item.description}</span>
                      <span className="opacity-60">
                        {format(new Date(item.created_at), 'h:mm a')}
                      </span>
                    </div>
                  </div>
                );
              }
              
              // Regular Message
              const isInbound = item.direction === 'inbound';
              const isCall = item.channel === 'call' || item.channel === 'voicemail';
              
              return (
                <div
                  key={item.id}
                  className={`flex gap-2 ${isInbound ? '' : 'flex-row-reverse'}`}
                >
                  {/* Avatar */}
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarFallback className="text-xs">
                      {isInbound 
                        ? customer?.first_name?.[0] || 'C'
                        : item.sender_name?.[0] || 'S'
                      }
                    </AvatarFallback>
                  </Avatar>
                  
                  {/* Message Bubble */}
                  <div className={`max-w-[70%] ${isInbound ? '' : 'text-right'}`}>
                    {/* Sender info */}
                    <div className={`flex items-center gap-2 mb-1 text-xs text-muted-foreground ${
                      isInbound ? '' : 'flex-row-reverse'
                    }`}>
                      <span className={getChannelColor(item.channel)}>
                        {getChannelIcon(item.channel)}
                      </span>
                      <span>
                        {isInbound 
                          ? `${customer?.first_name || 'Customer'}`
                          : item.sender_name || 'Staff'
                        }
                      </span>
                      <span className="opacity-60">
                        {format(new Date(item.created_at), 'h:mm a')}
                      </span>
                    </div>
                    
                    {/* Message Content */}
                    <div className={`rounded-lg p-3 ${
                      isInbound 
                        ? 'bg-muted text-foreground' 
                        : 'bg-primary text-primary-foreground'
                    }`}>
                      {/* Call/Voicemail specific UI */}
                      {isCall && (
                        <div className="mb-2">
                          <div className="flex items-center gap-2 mb-2">
                            {item.channel === 'voicemail' 
                              ? <Voicemail className="h-4 w-4" />
                              : <PhoneCall className="h-4 w-4" />
                            }
                            <span className="font-medium">
                              {item.channel === 'voicemail' ? 'Voicemail' : 'Phone Call'}
                            </span>
                            {item.duration_seconds && (
                              <Badge variant="outline" className="text-xs">
                                {Math.floor(item.duration_seconds / 60)}:{(item.duration_seconds % 60).toString().padStart(2, '0')}
                              </Badge>
                            )}
                          </div>
                          
                          {/* Audio Player */}
                          {item.audio_url && (
                            <div className="flex items-center gap-2 p-2 rounded bg-black/10">
                              <Button size="icon" variant="ghost" className="h-8 w-8">
                                <Play className="h-4 w-4" />
                              </Button>
                              <div className="flex-1 h-1 bg-black/20 rounded">
                                <div className="h-full w-0 bg-current rounded"></div>
                              </div>
                              <Button size="icon" variant="ghost" className="h-8 w-8">
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Message Body */}
                      {item.transcript ? (
                        <div>
                          <p className="text-xs font-medium mb-1 opacity-70">Transcript:</p>
                          <p className="whitespace-pre-wrap">{item.transcript}</p>
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap">{item.body}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </ScrollArea>
  );
};

export default MessageThread;